import { useEffect, useState } from "react";
import { BarChart3, ChevronRight, X } from "lucide-react";
import { api, fmtDate, fmtTL, ORDER_STATUS } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export default function ReportsTab() {
    const [suppliers, setSuppliers] = useState(null);
    const [stores, setStores] = useState(null);
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [statement, setStatement] = useState(null);
    const [view, setView] = useState("suppliers");
    const qs = () => `${from || to ? "?" : ""}${from ? `from_date=${from}` : ""}${from && to ? "&" : ""}${to ? `to_date=${to}` : ""}`;
    const load = () => {
        api.get(`/admin/reports/suppliers${qs()}`).then((r) => setSuppliers(r.data)).catch(() => {});
        api.get("/admin/stores").then((r) => setStores(r.data)).catch(() => {});
    };
    useEffect(() => { load(); }, []); // eslint-disable-line

    const openSupplierStatement = async (s) => {
        const r = await api.get(`/admin/reports/suppliers/${s.supplier_id}/statement${qs()}`);
        setStatement({ type: "supplier", ...r.data });
    };
    const openStoreStatement = async (c) => {
        const r = await api.get(`/admin/reports/stores/${c.company_id}/statement${qs()}`);
        setStatement({ type: "store", ...r.data });
    };

    const thisMonth = () => {
        const d = new Date();
        setFrom(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`);
        setTo("");
        setSuppliers(null);
        setTimeout(load, 0);
    };

    return (
        <div className="space-y-4" data-testid="admin-reports-tab">
            <div className="flex flex-wrap items-end gap-3 rounded-2xl border bg-card p-4">
                <div>
                    <p className="mb-1 text-xs font-semibold text-muted-foreground">Başlangıç</p>
                    <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" data-testid="report-from" />
                </div>
                <div>
                    <p className="mb-1 text-xs font-semibold text-muted-foreground">Bitiş</p>
                    <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" data-testid="report-to" />
                </div>
                <Button size="sm" onClick={() => { setSuppliers(null); load(); }} className="bg-terra-600 text-white hover:bg-terra-700" data-testid="report-apply">Uygula</Button>
                <Button size="sm" variant="outline" onClick={thisMonth} data-testid="report-this-month">Bu Ay</Button>
                <Button size="sm" variant="outline" onClick={() => { setFrom(""); setTo(""); setSuppliers(null); setTimeout(load, 0); }} data-testid="report-clear">Temizle</Button>
                <div className="ml-auto flex gap-2">
                    <button onClick={() => setView("suppliers")} className={`rounded-full border px-3.5 py-1.5 text-xs font-medium ${view === "suppliers" ? "border-primary bg-primary text-primary-foreground" : ""}`} data-testid="report-view-suppliers">Tedarikçiler</button>
                    <button onClick={() => setView("stores")} className={`rounded-full border px-3.5 py-1.5 text-xs font-medium ${view === "stores" ? "border-primary bg-primary text-primary-foreground" : ""}`} data-testid="report-view-stores">Mağazalar</button>
                </div>
            </div>

            {view === "suppliers" && (
                !suppliers ? <div className="h-64 animate-pulse rounded-2xl bg-muted" /> : (
                    <div className="overflow-hidden rounded-2xl border bg-card shadow-sm" data-testid="report-suppliers-table">
                        <table className="w-full text-sm">
                            <thead><tr className="border-b bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                                <th className="p-3">Tedarikçi (A-Z)</th><th className="p-3">Şehir</th><th className="p-3">Sipariş</th><th className="p-3">Ciro</th><th className="p-3 text-right">Ekstre</th>
                            </tr></thead>
                            <tbody>
                                {suppliers.map((s) => (
                                    <tr key={s.supplier_id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`report-supplier-${s.supplier_id}`}>
                                        <td className="p-3"><button onClick={() => openSupplierStatement(s)} className="font-semibold text-primary hover:underline" data-testid={`report-supplier-name-${s.supplier_id}`}>{s.name}</button></td>
                                        <td className="p-3 text-muted-foreground">{s.city}</td>
                                        <td className="p-3">{s.order_count}</td>
                                        <td className="p-3 font-display font-extrabold text-terra-600">{fmtTL(s.revenue)}</td>
                                        <td className="p-3 text-right"><Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => openSupplierStatement(s)} data-testid={`report-supplier-stmt-${s.supplier_id}`}>Ekstre <ChevronRight className="ml-1 h-3 w-3" /></Button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            {view === "stores" && (
                !stores ? <div className="h-64 animate-pulse rounded-2xl bg-muted" /> : (
                    <div className="overflow-hidden rounded-2xl border bg-card shadow-sm" data-testid="report-stores-table">
                        <table className="w-full text-sm">
                            <thead><tr className="border-b bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                                <th className="p-3">Mağaza (A-Z)</th><th className="p-3">Sipariş</th><th className="p-3">Toplam Alım</th><th className="p-3 text-right">Ekstre</th>
                            </tr></thead>
                            <tbody>
                                {[...stores].sort((a, b) => a.name.localeCompare(b.name, "tr")).map((c) => (
                                    <tr key={c.company_id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`report-store-${c.company_id}`}>
                                        <td className="p-3"><button onClick={() => openStoreStatement(c)} className="font-semibold text-primary hover:underline" data-testid={`report-store-name-${c.company_id}`}>{c.name}</button></td>
                                        <td className="p-3">{c.order_count}</td>
                                        <td className="p-3 font-display font-extrabold text-terra-600">{fmtTL(c.total_spend)}</td>
                                        <td className="p-3 text-right"><Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => openStoreStatement(c)} data-testid={`report-store-stmt-${c.company_id}`}>Ekstre <ChevronRight className="ml-1 h-3 w-3" /></Button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            {statement && (
                <div className="rounded-2xl border bg-card p-5 shadow-sm" data-testid="report-statement">
                    <div className="mb-4 flex items-center justify-between">
                        <div>
                            <h3 className="font-display text-lg font-bold">
                                {statement.type === "supplier" ? `${statement.supplier.name} — Mağaza Ekstresi` : `${statement.store.name} — Tedarikçi Ekstresi`}
                            </h3>
                            <p className="text-xs text-muted-foreground">{statement.orders.length} sipariş • toplam {fmtTL(statement.total)}</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => setStatement(null)} data-testid="statement-close"><X className="h-4 w-4" /></Button>
                    </div>
                    {statement.type === "supplier" && statement.orders.length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-2">
                            {Object.entries(statement.orders.reduce((acc, o) => { acc[o.buyer_company_name] = (acc[o.buyer_company_name] || 0) + o.total; return acc; }, {})).map(([k, v]) => (
                                <Badge key={k} variant="outline" className="text-xs">{k}: {fmtTL(v)}</Badge>
                            ))}
                        </div>
                    )}
                    {statement.type === "store" && statement.by_supplier?.length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-2">
                            {statement.by_supplier.map((s) => <Badge key={s.supplier} variant="outline" className="text-xs">{s.supplier}: {fmtTL(s.total)}</Badge>)}
                        </div>
                    )}
                    <div className="max-h-80 overflow-y-auto rounded-lg border">
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-muted/80"><tr className="border-b text-left"><th className="p-2">Sipariş</th><th className="p-2">{statement.type === "supplier" ? "Mağaza" : "Tedarikçi"}</th><th className="p-2">Tutar</th><th className="p-2">Durum</th><th className="p-2">Tarih</th></tr></thead>
                            <tbody>
                                {statement.orders.map((o) => (
                                    <tr key={o.order_id} className="border-b last:border-0" data-testid={`statement-order-${o.order_id}`}>
                                        <td className="p-2 font-mono">{o.order_no}</td>
                                        <td className="p-2">{statement.type === "supplier" ? o.buyer_company_name : o.supplier_name}</td>
                                        <td className="p-2 font-semibold">{fmtTL(o.total)}</td>
                                        <td className="p-2">{ORDER_STATUS[o.status]?.label || o.status}</td>
                                        <td className="p-2 text-muted-foreground">{fmtDate(o.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
